import { DateTime } from "luxon";
import { isOvernightShiftStartHour, parseTimeString } from "../../../utils/dateUtils";
import type { DepartmentLocation, NurseInShift } from "../../../utils/models";
import type { TimeSlot } from "../types";

/** `outer` contains `inner` (allowing equal endpoints). */
export const isTimeSlotContained = (outer: TimeSlot, inner: TimeSlot): boolean => {
	return outer.start <= inner.start && outer.end >= inner.end;
};

/** Two slots overlap by at least 1 hour. The 1-hour threshold is the gap-finder's required-vs-available match floor. */
export const doTimeSlotsOverlap = (a: TimeSlot, b: TimeSlot): boolean => {
	const overlapStart = DateTime.max(a.start, b.start);
	const overlapEnd = DateTime.min(a.end, b.end);
	return a.start < b.end && b.start < a.end && overlapEnd.diff(overlapStart, "hours").hours >= 1;
};

/**
 * Subtract `inner` from `outer`, returning the leftover `[before, after]` pieces.
 * Either piece may be `null` if `inner` aligns with `outer`'s edge on that side.
 */
export const splitTimeSlot = (outer: TimeSlot, inner: TimeSlot): [TimeSlot | null, TimeSlot | null] => {
	const beforePart: TimeSlot | null =
		outer.start < inner.start
			? { start: outer.start, end: inner.start, location: outer.location, roles: outer.roles, section: outer.section ?? inner.section, jobId: outer.jobId }
			: null;
	const afterPart: TimeSlot | null =
		outer.end > inner.end
			? { start: inner.end, end: outer.end, location: outer.location, roles: outer.roles, section: outer.section ?? inner.section, jobId: outer.jobId }
			: null;
	return [beforePart, afterPart];
};

/**
 * Convert a `DepartmentLocation` row's required-coverage window into a `TimeSlot` anchored to
 * the given date.
 *
 * Overnight-shift normalization: when the location's start hour is in the "overnight zone"
 * relative to the department's `dayStart`, anchor the slot to the correct calendar day:
 * - AM `dayStart` (e.g. 04:00): overnight start (e.g. 02:00) → next calendar day
 * - PM `dayStart` (e.g. 20:00): overnight start (e.g. 20:00, 22:00) → previous calendar day
 *
 * Also handles end-before-start (overnight-crossing) shifts by pushing `end` forward by one day.
 */
export const convertLocationToTimeSlot = (location: DepartmentLocation, date: DateTime, timezone: string, dayStart: string): TimeSlot => {
	const [startHour, startMin] = location.shift_start.split(":").map(Number);
	const [endHour, endMin] = location.shift_end.split(":").map(Number);

	let startTime = date.set({ hour: startHour, minute: startMin }).setZone(timezone, { keepLocalTime: true });
	let endTime = date.set({ hour: endHour, minute: endMin }).setZone(timezone, { keepLocalTime: true });

	if (isOvernightShiftStartHour(startTime, dayStart)) {
		const { hour: dsHour, minute: dsMinute } = parseTimeString(dayStart);
		const isAmDayStart = dsHour * 60 + dsMinute < 12 * 60;
		const shift = isAmDayStart ? { day: 1 } : { day: -1 };
		startTime = startTime.plus(shift);
		endTime = endTime.plus(shift);
	}

	if (endTime < startTime) {
		endTime = endTime.plus({ day: 1 });
	}

	return {
		start: startTime,
		end: endTime,
		location: location.location,
		roles: location.roles || ["all"],
		section: location.section,
		jobId: location.jobId || null,
	};
};

/**
 * Convert an assigned nurse shift into a `TimeSlot` for gap-finder matching.
 *
 * Normalizes start/end to the department's timezone so the gap-matcher's range comparisons are
 * in a single consistent zone. The nurse's stored DateTimes are typically already in that zone;
 * the explicit `setZone` is a safety net for callers that pass DateTimes in UTC or another zone.
 */
export const convertNurseToTimeSlot = (nurse: NurseInShift, timezone: string): TimeSlot => {
	return {
		start: nurse.startTime.setZone(timezone),
		end: nurse.endTime.setZone(timezone),
		location: nurse.location || "",
		roles: [nurse.role],
		section: "",
		jobId: nurse.jobId || null,
	};
};
